// src/pages/EditFinancialData.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface FinancialData {
  id: number | null;
  annualRevenue: string;
  newUnitsSold: string;
  avgSellingPrice: string;
  profitMargin: string;
  percentageOfTotal: string;
  numRepeatCustomers: string;
  totalPartsRevenue: string;
  mostSoldModels: string;
  avgTimeToClose: string;
  topCustomerGroup: string;
}

interface Report {
  id: number;
  created_at: string;
}

const EditFinancialData: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FinancialData>({
    id: null,
    annualRevenue: "",
    newUnitsSold: "",
    avgSellingPrice: "",
    profitMargin: "",
    percentageOfTotal: "",
    numRepeatCustomers: "",
    totalPartsRevenue: "",
    mostSoldModels: "",
    avgTimeToClose: "",
    topCustomerGroup: ""
  });

  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Utility to strip commas
  const sanitize = (val: string) => val.replace(/,/g, '');

  // 1) Fetch or create report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then(res => res.json())
      .then((data: Report[]) => {
        if (data.length) setNewestReportId(data[0].id);
        else {
          fetch(`/api/dealers/${id}/reports`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          })
            .then(r => r.json())
            .then((nr: Report) => setNewestReportId(nr.id))
            .catch(err => console.error('Error creating report', err));
        }
      })
      .catch(err => console.error('Error fetching reports', err));
  }, [id]);

  // 2) Load financial data
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/financial-data`)
      .then(res => res.json())
      .then((data: any[]) => {
        if (Array.isArray(data) && data.length) {
          const record = data[0];
          setFormData({
            id: record.id,
            annualRevenue: record.annual_revenue?.toString() || "",
            newUnitsSold: record.new_units_sold?.toString() || "",
            avgSellingPrice: record.avg_selling_price?.toString() || "",
            profitMargin: record.profit_margin?.toString() || "",
            percentageOfTotal: record.percentage_of_total?.toString() || "",
            numRepeatCustomers: record.num_repeat_customers?.toString() || "",
            totalPartsRevenue: record.total_parts_revenue?.toString() || "",
            mostSoldModels: record.most_sold_models || "",
            avgTimeToClose: record.avg_time_to_close?.toString() || "",
            topCustomerGroup: record.top_customer_group || ""
          });
        }
      })
      .catch(err => console.error('Error fetching financial data', err));
  }, [newestReportId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newestReportId) return console.error('No report found');

    const payload = {
      annualRevenue: formData.annualRevenue
        ? parseFloat(sanitize(formData.annualRevenue))
        : null,
      newUnitsSold: formData.newUnitsSold
        ? parseInt(sanitize(formData.newUnitsSold), 10)
        : null,
      avgSellingPrice: formData.avgSellingPrice
        ? parseFloat(sanitize(formData.avgSellingPrice))
        : null,
      profitMargin: formData.profitMargin
        ? parseFloat(sanitize(formData.profitMargin))
        : null,
      percentageOfTotal: formData.percentageOfTotal
        ? parseFloat(sanitize(formData.percentageOfTotal))
        : null,
      numRepeatCustomers: formData.numRepeatCustomers
        ? parseInt(sanitize(formData.numRepeatCustomers), 10)
        : null,
      totalPartsRevenue: formData.totalPartsRevenue
        ? parseFloat(sanitize(formData.totalPartsRevenue))
        : null,
      mostSoldModels: formData.mostSoldModels,
      avgTimeToClose: formData.avgTimeToClose
        ? parseInt(sanitize(formData.avgTimeToClose), 10)
        : null,
      topCustomerGroup: formData.topCustomerGroup
    };

    const url = `/api/reports/${newestReportId}/financial-data`;
    const options = {
      method: formData.id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        formData.id ? { id: formData.id, ...payload } : { dealer_id: parseInt(id!, 10), ...payload }
      )
    };

    fetch(url, options)
      .then(res => {
        if (!res.ok) return res.json().then(b => { throw new Error(b.error || 'Save failed'); });
        navigate(`/dealer/${id}`);
      })
      .catch(err => console.error('Error saving financial data', err));
  };

  // Styles
  const containerStyle: React.CSSProperties = { backgroundColor: '#F0F5F0', minHeight: '100vh', padding: '20px' };
  const cardStyle: React.CSSProperties = { backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', maxWidth: '800px', margin: '30px auto', padding: '30px' };
  const headerStyle: React.CSSProperties = { textAlign: 'center', color: '#e41e26', borderBottom: '2px solid #e41e26', paddingBottom: '10px', marginBottom: '20px', fontSize: '24px' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '5px', fontWeight: 'bold' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' };
  const buttonStyle: React.CSSProperties = { padding: '5px 10px', backgroundColor: '#e41e26', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Financial Data</h1>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Annual Revenue:</label>
          <input
            type="text"
            name="annualRevenue"
            style={inputStyle}
            value={formData.annualRevenue}
            onChange={handleChange}
            placeholder="e.g. 2541123 or 2,541,123"
          />

          <label style={labelStyle}>New Units Sold (Last 12 Months):</label>
          <input
            type="number"
            name="newUnitsSold"
            step="1"
            style={inputStyle}
            value={formData.newUnitsSold}
            onChange={handleChange}
          />

          <label style={labelStyle}>Average Selling Price per Unit:</label>
          <input
            type="text"
            name="avgSellingPrice"
            style={inputStyle}
            value={formData.avgSellingPrice}
            onChange={handleChange}
            placeholder="e.g. 49999.99"
          />

          <label style={labelStyle}>Profit Margin:</label>
          <input
            type="text"
            name="profitMargin"
            style={inputStyle}
            value={formData.profitMargin}
            onChange={handleChange}
            placeholder="e.g. 12.5"
          />

          <label style={labelStyle}>Percentage of Total Business:</label>
          <input
            type="text"
            name="percentageOfTotal"
            style={inputStyle}
            value={formData.percentageOfTotal}
            onChange={handleChange}
            placeholder="e.g. 33.3"
          />

          <label style={labelStyle}>Number of Repeat Customers:</label>
          <input
            type="number"
            name="numRepeatCustomers"
            step="1"
            style={inputStyle}
            value={formData.numRepeatCustomers}
            onChange={handleChange}
          />

          <label style={labelStyle}>Total Parts Revenue:</label>
          <input
            type="text"
            name="totalPartsRevenue"
            style={inputStyle}
            value={formData.totalPartsRevenue}
            onChange={handleChange}
            placeholder="e.g. 123456"
          />

          <label style={labelStyle}>Most Sold Model(s):</label>
          <input
            type="text"
            name="mostSoldModels"
            style={inputStyle}
            value={formData.mostSoldModels}
            onChange={handleChange}
          />

          <label style={labelStyle}>Average Time to Close a Sale (days):</label>
          <input
            type="number"
            name="avgTimeToClose"
            step="1"
            style={inputStyle}
            value={formData.avgTimeToClose}
            onChange={handleChange}
          />

          <label style={labelStyle}>Top Customer Group:</label>
          <input
            type="text"
            name="topCustomerGroup"
            style={inputStyle}
            value={formData.topCustomerGroup}
            onChange={handleChange}
          />

          <div style={{ marginTop: '20px' }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFinancialData;
